import { UserService } from '@acme/api/client';
import { useUser } from '@acme/domains';
import { PasswordInput } from '@acme/foundation';
import { Button, Card, Label } from 'flowbite-react';
import { useState } from 'react';
import { HiPencil, HiPlus } from 'react-icons/hi';

export const TwoFactorAuthentication = () => {
  const { user } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const isTwoFactor = user?.isTwoFactor || false;

  const [form, setForm] = useState({
    oldPassword: '',
    password: '',
    repeat: '',
  });

  const handleChange = (
    field: 'oldPassword' | 'password' | 'repeat',
    value: string
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setForm({ oldPassword: '', password: '', repeat: '' });
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);

    if (isTwoFactor) {
      if (!form.oldPassword.trim()) {
        setError('Введите старый пароль');
        return;
      }
    }

    if (!form.password.trim()) {
      setError('Введите новый пароль');
      return;
    }

    if (form.password !== form.repeat) {
      setError('Пароли не совпадают');
      return;
    }

    setIsLoading(true);

    try {
      await UserService.putApiV1UserTwoFactor({
        requestBody: {
          isTwoFactor: true,
          twoFactorPassword: form.password,
          oldTwoFactorPassword: isTwoFactor ? form.oldPassword : null,
        },
      });

      setIsEditing(false);
      setForm({ oldPassword: '', password: '', repeat: '' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка при сохранении пароля');
    } finally {
      setIsLoading(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex flex-col gap-0">
        <Card className="rounded-b-none border-b-0">
          <div className="flex flex-col gap-6">
            <h3 className="text-xl font-semibold">
              Двухфакторная аутентификация (2FA)
            </h3>

            <div
              className={`grid ${
                isTwoFactor ? 'grid-cols-3' : 'grid-cols-2'
              } gap-6`}
            >
              {isTwoFactor && (
                <div>
                  <Label htmlFor="oldPassword">Введите старый пароль</Label>
                  <PasswordInput
                    id="oldPassword"
                    value={form.oldPassword}
                    onChange={(v) => handleChange('oldPassword', v)}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="password">
                  {isTwoFactor ? 'Новый пароль' : 'Пароль'}
                </Label>
                <PasswordInput
                  id="password"
                  value={form.password}
                  onChange={(v) => handleChange('password', v)}
                />
              </div>
              <div>
                <Label htmlFor="repeat">
                  {isTwoFactor ? 'Новый пароль ещё раз' : 'Пароль ещё раз'}
                </Label>
                <PasswordInput
                  id="repeat"
                  value={form.repeat}
                  onChange={(v) => handleChange('repeat', v)}
                />
              </div>
            </div>

            <div className="hidden" />

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                {error}
              </div>
            )}
          </div>
        </Card>

        <Card className="rounded-t-none border-t-0">
          <div className="flex items-center justify-between">
            <div></div>
            <div className="flex gap-4">
              <Button color="secondary" onClick={handleCancel} outline>
                Отмена
              </Button>
              <Button
                color="alternative"
                disabled={isLoading}
                onClick={handleSubmit}
              >
                {isLoading
                  ? 'Сохранение...'
                  : isTwoFactor
                    ? 'Сохранить'
                    : 'Включить'}{' '}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">
          Двухфакторная аутентификация (2FA)
        </h3>
        {isTwoFactor ? (
          <Button onClick={() => setIsEditing(true)} color="alternative">
            <HiPencil className="mr-2 h-3 w-3" />
            Изменить пароль
          </Button>
        ) : (
          <Button color="alternative" onClick={() => setIsEditing(true)}>
            <HiPlus className="mr-2 h-5 w-5" />
            Включить
          </Button>
        )}
      </div>
    </Card>
  );
};
